'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input
} from '@goodneighbor/ui';
import { useMutation } from '@tanstack/react-query';
import { forgotPassword, verifyForgotPassword } from '@/services/webApi';
import styles from './partnerForgotPasswordForm.module.scss';
import { ForgotFormValues } from '@/app/volunteer/(auth)/forgot-password/ForgotForm';
import { forgotBodySchema } from '@goodneighbor/api/schemas/auth';
import { useState } from 'react';
import OtpForm from '@/app/partner/components/OtpForm';
import { useOtpForm } from '@/app/partner/components/OtpForm/useOtpForm';
import ResetPasswordForm from '@/app/volunteer/(auth)/reset-password/ResetPasswordForm';
import { FormSteps } from '@/app/partner/forgot-password/PartnerForgotPasswordContent';

export default function PartnerForgotPasswordForm({
  step,
  setStep
}: {
  step: FormSteps;
  setStep: (step: FormSteps) => void;
}) {
  const [resetToken, setResetToken] = useState<string>('');
  const {
    otpValue,
    otpVerificationError,
    setOtpVerificationError,
    handleOtpChange,
    resetOtp,
    otpLength
  } = useOtpForm();

  const forgotMutation = useMutation({
    mutationFn: async (vars: ForgotFormValues) => {
      return forgotPassword({ ...vars, intent: 'partner' });
    },
    onSuccess: () => {
      setStep('otp');
    }
  });

  const verifyOtpMutation = useMutation({
    mutationFn: verifyForgotPassword,
    onSuccess: (data) => {
      if (data) {
        setResetToken(data.resetToken);
        setStep('reset');
      }
    },
    onError: (error) => {
      setOtpVerificationError(error.message);
    }
  });

  const form = useForm<ForgotFormValues>({
    resolver: zodResolver(forgotBodySchema),
    defaultValues: {
      email: ''
    }
  });

  const onSubmit = (data: ForgotFormValues) => {
    forgotMutation.mutate(data);
  };

  if (step === 'email') {
    return (
      <Form {...form}>
        <form className={styles.form} onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className={styles.textField}>
                <FormControl>
                  <Input type="email" placeholder="Email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            size="lg"
            className={styles.submitButton}
            type="submit"
            isLoading={forgotMutation.isPending}
            isDisabled={!form.formState.isValid || forgotMutation.isSuccess}
          >
            Send reset instructions
          </Button>
          {forgotMutation.isError && (
            <Alert
              className={styles.infoCard}
              color="error"
              title="Something went wrong"
              description={forgotMutation.error.message}
            />
          )}
        </form>
      </Form>
    );
  } else if (step === 'otp') {
    return (
      <OtpForm
        otpValue={otpValue}
        handleOtpChange={handleOtpChange}
        otpLength={otpLength}
        verificationError={otpVerificationError}
        isVerifyOtpPending={verifyOtpMutation.isPending}
        handleResend={async () => {
          resetOtp();
          setOtpVerificationError(null);
          try {
            await forgotMutation.mutateAsync({
              email: form.getValues('email')
            });
            return true;
          } catch {
            return false;
          }
        }}
        isSendOtpPending={forgotMutation.isPending}
        email={form.getValues('email')}
        onSubmit={(e) => {
          e.preventDefault();
          verifyOtpMutation.mutate({
            email: form.getValues('email'),
            otp: otpValue
          });
        }}
      />
    );
  } else if (step === 'reset') {
    return (
      <ResetPasswordForm email={form.getValues('email')} token={resetToken} />
    );
  }
}
